import React from 'react'
import { makeStyles } from '@material-ui/core'
import VerbalColorType from '../../helpers/types/VerbalColor.interface'
import style from './style'

/**
 *
 */
export interface ProgressType {
    variant?: 'linear'
    bufferColor?: VerbalColorType
    bgColor?: VerbalColorType
    color?: VerbalColorType
    height?: number
    min?: number
    max?: number
    value: number
    duration?: boolean | { duration: string | number }
    className?: string
    'aria-label'?: string
    'aria-labelledby'?: string
    'data-testid'?: string
}

const useStyles = makeStyles(style)

/** Displays a value within a bounded range as a linear progress bar. */
const Progress = ({
    variant = 'linear',
    bufferColor,
    bgColor,
    color,
    height = 4,
    min = 0,
    max = 100,
    value,
    duration = true,
    className,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledBy,
    'data-testid': dataTestId = 'progress'
}: ProgressType): JSX.Element => {
    const styles = useStyles()
    const colorFamily = (color || 'primary').split('-')[0]
    const background = color ? `${colorFamily}-300` : bgColor || 'primary-300'
    const buffer = color ? `${colorFamily}-600` : bufferColor || 'primary-600'
    const minimum = Number.isFinite(min) ? min : 0
    const maximum = Number.isFinite(max) ? Math.max(minimum, max) : Math.max(minimum, 100)
    const currentValue = Number.isNaN(value) ? minimum : Math.min(maximum, Math.max(minimum, value))
    const percentage = maximum > minimum ? (currentValue - minimum) / (maximum - minimum) * 100 : 0
    const transitionDuration = typeof duration === 'object' ? duration.duration : 300
    const transition = duration === false
        ? 'none'
        : `width ${typeof transitionDuration === 'number' ? `${transitionDuration}ms` : transitionDuration} ease`

    return (
        <div
            role='progressbar'
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledBy}
            aria-valuemin={minimum}
            aria-valuemax={maximum}
            aria-valuenow={currentValue}
            className={`${styles.progress} progress--${variant} progress--bg-${background} ${className || ''}`}
            style={{ height }}
            data-testid={dataTestId}
        >
            <div
                className={`${styles.buffer} progress--buffer-${buffer}`}
                style={{ width: `${percentage}%`, transition }}
                data-testid={`${dataTestId}-buffer`}
            />
        </div>
    )
}

export default Progress
